import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import axiosRetry from 'axios-retry';

import Cookies from 'js-cookie';
import accessToken, {
  getCvAccessToken
} from '~/purchase-ui/src/helpers/accessToken';
import { getRequestHeaders } from './request-headers';

declare global {
  interface Window {
    CarvanaEvents: { emitter: { emit: (_: string) => void } };
  }
}

function uuid(seed: string | null = null): string {
  const seedInt = seed === null ? null : parseInt(seed);
  return seedInt !== null
    ? (seedInt ^ ((Math.random() * 16) >> (seedInt / 4))).toString(16)
    : `${1e7}${-1e3}${-4e3}${-8e3}${-1e11}`.replace(/[018]/g, uuid);
}

const tokenRefreshUrl = `${process.env.BASE_URL}${process.env.CVNA_APP_AUTH_TOKEN}`;

const axiosInstance = axios.create({
  baseURL: process.env.BASE_URL,
  maxRedirects: 0,
  headers: getRequestHeaders()
});

axiosRetry(axiosInstance, {
  retries: 0,
  retryDelay: retryCount => retryCount * 1000
});

const fetchAuthToken = () =>
  axiosInstance
    .post(tokenRefreshUrl, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
    .then(res => {
      return res.data;
    })
    .catch(async error => {
      const { response: { status = null } = {} } = error;
      const isStatusFourHundred = status >= 400;

      if (isStatusFourHundred) {
        await axiosInstance.post('/user-logout').catch(() => {
          for (const token of ['CVAccessToken', 'CVIdToken']) {
            Cookies.remove(token);
          }
          window.location.href = process.env.BASE_URL + '/purchase';
        });
      }
    });

const fetchAndSaveToken = async () => {
  await fetchAuthToken();
  return getCvAccessToken();
};

export const refreshTokenIfMissing = async () => {
  const { cvIdToken, cvAccessToken, isValidToken } = accessToken();

  if (cvIdToken && !cvAccessToken && !isValidToken) {
    await fetchAndSaveToken();
  }
};

axiosInstance.interceptors.request.use(
  async config => {
    const originalConfig = { headers: {}, ...config };
    originalConfig.headers['correlation-id'] = uuid();
    const token = accessToken();
    let cvAccessToken = token.cvAccessToken;

    if (cvAccessToken)
      originalConfig.headers.Authorization = `Bearer ${cvAccessToken}`;

    if (
      originalConfig.method === 'get' &&
      /MSIE \d|Trident.*rv:/.test(navigator.userAgent)
    ) {
      // add a random query param to remove caching on ie11
      originalConfig.url += `?${new Date().getTime()}`;
    }

    return originalConfig;
  },
  error => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  async response => response,
  async error => {
    const { response: { status = null } = {} } = error;

    const originalRequest = error.config;
    if (originalRequest.url === tokenRefreshUrl) {
      return Promise.reject(error);
    }

    if (status === 401) {
      if (originalRequest.isRetry) {
        throw error;
      } else {
        originalRequest.isRetry = true;
        const currentToken = getCvAccessToken();
        let newToken = await fetchAndSaveToken();

        if (newToken === currentToken) {
          datadogRum.addError(new Error('Same token after refresh, delaying'));
          await new Promise<void>(resolve => setTimeout(() => resolve(), 150));
          newToken = getCvAccessToken();
        }

        if (newToken === currentToken) {
          datadogRum.addError(new Error('Same token after refresh and delay'));
        }

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
